import {
    RaceConfiguration,
    Round,
    StorageFile,
    StorageFileFormDefault,
} from "api";
import { blue, red } from "@material-ui/core/colors";
import groupBy from "lodash/groupBy";

export enum ChampionshipStatus {
    CREATED = "CREATED",
    IN_PROGRESS = "IN_PROGRESS",
    DONE = "DONE",
}

export const ChampionshipStatusDescription = {
    CREATED: "Não iniciado",
    IN_PROGRESS: "Em andamento",
    DONE: "Finalizado",
};

export enum ChampionshipType {
    ROAD = "ROAD",
    OVAL = "OVAL",
}

export const ChampionshipTypeDescription = {
    ROAD: "Road",
    OVAL: "Oval",
};

export const ChampionshipTypeColor = {
    ROAD: red["800"],
    OVAL: blue["700"],
};

export interface Championship {
    id: string;
    name: string;
    description?: string;
    championshipType?: ChampionshipType;
    public: boolean;
    rounds: Round[];
    raceConfigurations: RaceConfiguration[];
    status: ChampionshipStatus;
    seasonId: string;
    isMultiClass: boolean;
    isFixedSetup: boolean;
    championshipByTeam: boolean;
    cars: ChampionshipCar[];
    image?: StorageFile;
    discards: string;
    amountDriversByTeam: string;
    driversToScore: string;
    driversStandings?: DriverStandings[];
    teamsStandings?: TeamStandings[];
    safeDrivers?: SafeDriver[];
    fastLapBonusPoint: number;
    poleBonusPoint: number;
    safeDriverBonusPoint: number;
    gainPositionsBonusPoint: number;
    oneLapLedBonusPoint: number;
    lapsLedBonusPoint: number;
    completedLapsToScore: number;
    losesPointsChangingCar: boolean;
    infractionSettings?: InfractionSettings;
}

export interface ChampionshipCar {
    id: string;
    brandId: string;
    name: string;
    model: string;
    carIRacingID: string;
    className: string;
    classIRacingID: string;
}

export interface DriverStandings {
    raceId: string;
    position: string;
    carID: string;
    carName: string;
    classID: string;
    className: string;
    teamID: string;
    teamName: string;
    driverId: string;
    driverName: string;
    carNumber: string;
    iRacingId: string;
    points: number;
    numberOfPoles: number;
    wins: number;
    top5: number;
    top10: number;
    numberOfRaces: number;
    numberOfRounds: number;
    standingsByRound: [
        {
            roundId: string;
            position: number;
        }
    ];
    withoutDiscards: {
        position: string;
        points: number;
        numberOfPoles: number;
        wins: number;
        top5: number;
        top10: number;
        numberOfRaces: number;
        numberOfRounds: number;
        standingsByRound: [
            {
                roundId: string;
                position: number;
            }
        ];
    };
}

export interface TeamStandings {
    driversIds: [string];
    teamID: string;
    teamName: string;
    className: string;
    oldPosition: number;
    position: number;
    points: number;
}

interface SafeDriver {
    iRacingId: string;
    lapsComplete: string;
    incidents: number;
    ratio: number;
    percentageIncidents: number;
    percentageOfMaxLapsComplete: number;
}

interface InfractionSettings {
    maxPointsToSuspend?: number;
    maxPointsToStartFromPitLane?: number;
}

export const ChampionshipDefaultValues: Championship = {
    id: "",
    name: "",
    description: "",
    championshipType: ChampionshipType.ROAD,
    status: ChampionshipStatus.CREATED,
    public: false,
    raceConfigurations: [],
    rounds: [],
    seasonId: "",
    isMultiClass: false,
    isFixedSetup: false,
    cars: [],
    image: StorageFileFormDefault,
    discards: "0",
    amountDriversByTeam: "0",
    driversToScore: "0",
    driversStandings: [],
    safeDrivers: [],
    fastLapBonusPoint: 0,
    poleBonusPoint: 0,
    safeDriverBonusPoint: 0,
    gainPositionsBonusPoint: 0,
    oneLapLedBonusPoint: 0,
    lapsLedBonusPoint: 0,
    championshipByTeam: true,
    completedLapsToScore: 50,
    losesPointsChangingCar: false,
    infractionSettings: {
        maxPointsToSuspend: 6,
        maxPointsToStartFromPitLane: 4,
    }
};

export function convertToClasses(cars: ChampionshipCar[]) {
    const carsGroupedByClassId = groupBy(cars, (car) => car.classIRacingID);
    return Object.keys(carsGroupedByClassId).map((id) => ({
        id,
        name: carsGroupedByClassId[id][0].className,
    }));
}
